// Formats are editorial metadata: never infer paper length from venue or page count.
export const publicationTypes = [
  ['full paper', 'Artigos completos', 'Full papers', 'Trabalhos publicados no formato de artigo completo.', 'Works published in full-paper format.'],
  ['short paper', 'Artigos curtos', 'Short papers'],
  ['workshop paper', 'Artigos de workshop', 'Workshop papers'],
  ['tool room', 'Demonstrações de ferramentas', 'Tool demonstrations'],
  ['preprint', 'Preprints', 'Preprints'],
  ['thesis', 'Teses e trabalhos acadêmicos', 'Theses and dissertations'],
  ['technical report', 'Relatórios técnicos', 'Technical reports'],
  ['patent', 'Patentes', 'Patents'],
  ['other', 'Outros trabalhos', 'Other works'],
];
export function publicationType(pub) {
  const tags = Array.isArray(pub.tags) ? pub.tags : [pub.tags || ''];
  if (tags.includes('extended abstract')) return 'short paper';
  if (tags.some(tag => tag.includes('thesis'))) return 'thesis';
  return publicationTypes.find(([key]) => tags.includes(key))?.[0] || 'other';
}
// Language is an editorial tag on the entry ("pt-br"); anything untagged is written in English.
export const publicationLanguages = [
  ['pt-br', 'Português', 'Portuguese'],
  ['en', 'Inglês', 'English'],
];
export function publicationLanguage(pub) {
  const tags = Array.isArray(pub.tags) ? pub.tags : [pub.tags || ''];
  return tags.some(tag => /^pt(-br)?$/i.test(String(tag).trim())) ? 'pt-br' : 'en';
}
// Only the first given name and the last surname are spelled out; the names in between are
// reduced to initials. Particles ("de", "dos", "van") are not names, so they are never turned
// into initials, and a generational suffix stays attached to the surname it qualifies.
const PARTICLES = new Set(['da', 'das', 'de', 'del', 'della', 'di', 'do', 'dos', 'du', 'e', 'la', 'le', 'van', 'von', 'der', 'den', 'y']);
const SUFFIXES = new Set(['jr', 'junior', 'júnior', 'neto', 'filho', 'sobrinho', 'ii', 'iii', 'iv']);
const tokens = value => String(value || '').trim().split(/\s+/).filter(Boolean);
const isParticle = token => PARTICLES.has(token.toLowerCase());
const initial = token => token.endsWith('.') ? token : `${[...token][0]}.`;

const isSuffix = token => SUFFIXES.has(token.toLowerCase().replace(/\.$/, ''));

function shortGiven([first, ...rest]) {
  if (!first) return '';
  return [first, ...rest.filter(token => !isParticle(token)).map(initial)].join(' ');
}

function shortFamily(parts) {
  if (!parts.length) return '';
  let surname = parts.length - 1;
  while (surname > 0 && isSuffix(parts[surname])) surname -= 1;
  return parts
    .map((token, index) => index >= surname || isParticle(token) ? token : initial(token))
    .join(' ');
}

export function authorName(author) {
  const given = tokens(author?.given);
  const family = tokens(author?.family);
  // Some entries record only a generational suffix as the family name ("Junior, Luis Carlos
  // Vendramin"), so the surname to keep is the last given name.
  if (family.length && family.every(isSuffix) && given.length > 1) family.unshift(given.pop());
  return [shortGiven(given), shortFamily(family)].filter(Boolean).join(' ');
}
export const authorList = authors => (authors || []).map(authorName).filter(Boolean).join(', ');
export const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const publicationYear = pub => String(pub.issued?.['date-parts']?.[0]?.[0] || '—');
export const publicationVenue = pub => pub['container-title'] || pub.publisher || '';
export { translateVenue } from './venueTranslations';
// Portuguese entries carry an editorial English version alongside the published one. The title
// as published is never replaced, only demoted to a second line, because it is the title the
// work is indexed and cited under.
export const localizedTitle = (entry, isPortuguese) =>
  isPortuguese ? entry.title : (entry.titleEn || entry.title);
export const originalTitle = (entry, isPortuguese) =>
  !isPortuguese && entry.titleEn && entry.titleEn !== entry.title ? entry.title : null;
export const localizedAbstract = (entry, isPortuguese) =>
  isPortuguese ? entry.abstract : (entry.abstractEn || entry.abstract);

// BibTeX entries separate keywords with either commas or semicolons, sometimes both.
export const publicationKeywords = (pub, isPortuguese = true) => {
  const words = String((isPortuguese ? pub.keyword : pub.keywordsEn || pub.keyword) || '')
    .split(/[,;]+/)
    .map(word => word.trim())
    .filter(Boolean);
  const seen = new Set();
  return words.filter(word => {
    const key = normalize(word);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
export function filterPublications(publications, { query = '', type = '', year = '', venue = '', topic = '' }) {
  const words = normalize(query).trim().split(/\s+/).filter(Boolean);
  return publications.filter(pub => (!type || publicationType(pub) === type)
    && (!topic || (pub.topics || []).includes(topic))
    && (!year || publicationYear(pub) === year)
    && (!venue || publicationVenue(pub) === venue)
    && words.every(word => normalize([pub.title, pub.titleEn, pub.abstract, pub.abstractEn, pub.keyword, pub.keywordsEn, pub.id, publicationYear(pub), publicationVenue(pub), ...(pub.tags || []), ...(publicationTypes.find(([key]) => key === publicationType(pub)) || []), ...(pub.author || []).map(a => `${a.given || ''} ${a.family || ''}`)].join(' ')).includes(word)));
}
